/* Nova desktop dropdown controller. Does not replace Xclusive's mobile drawer.
   Instances reconnect after Shopify section rendering. Element-keyed state
   avoids inheriting stale initialization from the theme's cloned menu items. */
(() => {
  'use strict';
  if (window.NovaDropdownMenu) {
    window.NovaDropdownMenu.init();
    return;
  }
  const desktop = window.matchMedia('(min-width: 1001px)');
  const roots = new Map();
  const wrappers = new WeakSet();
  let serial = 0;
  let touchUntil = 0;
  let focusGuard = false;
  let frame = 0;
  const rootSelector = '#header-outer[data-nova-dropdown] :is(#nav, #nav-bar) > ul[data-type] > .nv-dropdown-item';
  const direct = (node, selector) => node.querySelector(':scope > ' + selector);
  const active = () => desktop.matches && !document.documentElement.classList.contains('m2a');
  const touch = event => event.pointerType === 'touch' || Date.now() < touchUntil;
  const firstLink = panel => panel.querySelector('a:not(.toggle)');
  const controls = node => Array.from(node.children).filter(el => el.matches('a.toggle:not(.toggle-back)'));

  function setExpanded(node, expanded) {
    controls(node).forEach(control => control.setAttribute('aria-expanded', String(expanded)));
  }
  function focus(element) {
    if (!element) return;
    focusGuard = true;
    element.focus({ preventScroll: true });
    focusGuard = false;
  }
  function closeChild(category) {
    clearTimeout(category._nvCloseTimer);
    category.classList.remove('nv-open', 'toggle');
    setExpanded(category, false);
  }
  function close(root) {
    const state = roots.get(root);
    if (!state) return;
    clearTimeout(state.timer);
    root.classList.remove('nv-open', 'toggle');
    setExpanded(root, false);
    state.categories.forEach(closeChild);
  }
  function fit(root) {
    const state = roots.get(root);
    if (!state || !active()) return;
    const { panel } = state;
    const viewport = document.documentElement.clientWidth;
    const bounds = root.getBoundingClientRect();
    const width = panel.getBoundingClientRect().width;
    const left = Math.max(12, Math.min(bounds.left - 20, viewport - width - 12));
    root.style.setProperty('--nv-panel-shift', (left - bounds.left + 20) + 'px');
    const rtl = getComputedStyle(root).direction === 'rtl';
    const canRight = left + width * 2 <= viewport - 12;
    const canLeft = left - width >= 12;
    root.dataset.nvSide = (rtl ? canLeft : !canRight && canLeft) ? 'left' : 'right';
    const available = Math.max(80, window.innerHeight - panel.getBoundingClientRect().top - 12);
    root.style.setProperty('--nv-panel-max-height', available + 'px');
    // Compare natural list height without any expanded grandchildren.
    const naturalHeight = state.categories.reduce((sum, category) => {
      const link = direct(category, 'a:not(.toggle)');
      return sum + (link ? link.getBoundingClientRect().height : 0);
    }, 22);
    root.classList.toggle('nv-long-panel', naturalHeight > available);
  }
  function fitChild(root, category) {
    if (!active() || root.classList.contains('nv-long-panel')) return;
    const panel = direct(category, '.nv-dropdown-flyout');
    if (!panel) return;
    panel.style.setProperty('--nv-flyout-max-height', Math.max(80, window.innerHeight - 24) + 'px');
    const bounds = category.getBoundingClientRect();
    const height = panel.getBoundingClientRect().height;
    const top = Math.max(12, Math.min(bounds.top - 1, window.innerHeight - height - 12));
    panel.style.setProperty('--nv-flyout-top', (top - bounds.top) + 'px');
  }
  function open(root) {
    if (!active()) return;
    const state = roots.get(root);
    if (!state) return;
    clearTimeout(state.timer);
    roots.forEach((_, other) => { if (other !== root) close(other); });
    root.classList.add('nv-open');
    setExpanded(root, true);
    fit(root);
  }
  function openChild(root, category) {
    if (!active()) return;
    open(root);
    roots.get(root).categories.forEach(other => { if (other !== category) closeChild(other); });
    clearTimeout(category._nvCloseTimer);
    if (!direct(category, '.nv-dropdown-flyout')) return;
    category.classList.add('nv-open');
    setExpanded(category, true);
    fitChild(root, category);
  }
  function installDisclosure(node, panel) {
    panel.id = 'NovaDropdown-' + (++serial);
    controls(node).forEach(control => {
      control.setAttribute('role', 'button');
      control.setAttribute('aria-controls', panel.id);
      if (active()) control.setAttribute('aria-expanded', 'false');
      else control.removeAttribute('aria-expanded');
      const label = direct(node, 'a:not(.toggle)');
      if (label) control.setAttribute('aria-label', label.textContent.trim());
    });
  }
  function connect(root) {
    if (roots.has(root)) return;
    const panel = direct(root, '.nv-dropdown-panel');
    if (!panel) return;
    const categories = Array.from(panel.querySelectorAll('.nv-dropdown-category'));
    const state = { panel, categories, timer: 0 };
    roots.set(root, state);
    root.dataset.nvReady = 'true';
    installDisclosure(root, panel);
    categories.forEach(category => {
      const child = direct(category, '.nv-dropdown-flyout');
      if (child) installDisclosure(category, child);
      category.addEventListener('pointerenter', event => {
        if (active() && !touch(event)) openChild(root, category);
      });
      category.addEventListener('pointerleave', event => {
        if (!active() || touch(event)) return;
        category._nvCloseTimer = setTimeout(() => {
          if (!category.contains(document.activeElement)) closeChild(category);
        }, 170);
      });
    });
    root.addEventListener('pointerenter', event => {
      if (active() && !touch(event)) open(root);
    });
    root.addEventListener('pointerleave', event => {
      if (!active() || touch(event)) return;
      state.timer = setTimeout(() => {
        if (!root.contains(document.activeElement)) close(root);
      }, 190);
    });
    root.addEventListener('focusin', event => {
      if (!active() || focusGuard) return;
      if (panel.contains(event.target)) {
        open(root);
        const category = event.target.closest('.nv-dropdown-category');
        if (category) openChild(root, category);
      }
    });
    root.addEventListener('focusout', () => {
      setTimeout(() => {
        if (active() && !root.contains(document.activeElement) && !root.matches(':hover')) close(root);
      }, 0);
    });
  }
  function handleClick(event) {
    if (!active()) return;
    const link = event.target.closest('a');
    if (!link) return;
    const root = link.closest('.nv-dropdown-item');
    if (!roots.has(root)) return;
    const category = link.closest('.nv-dropdown-category');
    const parent = category || root;
    const panel = direct(parent, category ? '.nv-dropdown-flyout' : '.nv-dropdown-panel');
    // Actual leaf links and clickable parent URLs always retain navigation.
    if (!panel || link.parentElement !== parent) return;
    const placeholder = link.getAttribute('href') === '#' || link.getAttribute('href') === '';
    const control = link.classList.contains('toggle') || placeholder || (!category && root.classList.contains('inactive'));
    const touchToOpen = touch(event) && !parent.classList.contains('nv-open');
    if (!control && !touchToOpen) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const wasOpen = parent.classList.contains('nv-open');
    if (wasOpen && control) {
      if (category) closeChild(category); else close(root);
    } else if (category) openChild(root, category);
    else open(root);
  }
  function handleKey(event) {
    if (!active()) return;
    const root = event.target.closest('.nv-dropdown-item');
    if (!roots.has(root)) return;
    const target = event.target.closest('a');
    if (!target) return;
    const state = roots.get(root);
    const category = target.closest('.nv-dropdown-category');
    const flyout = target.closest('.nv-dropdown-flyout');
    const rtl = getComputedStyle(root).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    let handled = true;
    if (event.key === 'Escape') {
      if (category && category.classList.contains('nv-open')) {
        closeChild(category);
        focus(direct(category, 'a:not(.toggle)'));
      } else {
        close(root);
        focus(direct(root, 'a:not(.toggle)'));
      }
    } else if (event.key === ' ' && target.classList.contains('toggle')) {
      target.click();
    } else if (category && event.key === forward && !flyout && direct(category, '.nv-dropdown-flyout')) {
      openChild(root, category);
      focus(firstLink(direct(category, '.nv-dropdown-flyout')));
    } else if (flyout && event.key === back) {
      closeChild(category);
      focus(direct(category, 'a:not(.toggle)'));
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      if (!state.panel.contains(target)) {
        open(root);
        const items = state.categories.map(item => direct(item, 'a:not(.toggle)')).filter(Boolean);
        focus(event.key === 'ArrowUp' || event.key === 'End' ? items.at(-1) : items[0]);
      } else {
        const items = flyout ? Array.from(flyout.querySelectorAll(':scope > li > a:not(.toggle)'))
          : state.categories.map(item => direct(item, 'a:not(.toggle)')).filter(Boolean);
        let index = items.indexOf(target);
        if (index === -1 && category) index = items.indexOf(direct(category, 'a:not(.toggle)'));
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = items.length - 1;
        else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        if (items[index]) {
          if (!flyout) openChild(root, items[index].closest('.nv-dropdown-category'));
          focus(items[index]);
        }
      }
    } else handled = false;
    if (handled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
  function preserveOverflowMenu(wrapper) {
    // Native "View more" copies the horizontal menu. Leave that overflow
    // control on Xclusive's original markup/styles, without cloned IDs.
    wrapper.querySelectorAll('.show-all .nv-dropdown-item').forEach(copy => {
      [copy, ...copy.querySelectorAll('*')].forEach(node => {
        Array.from(node.classList).filter(name => name.startsWith('nv-dropdown-') || name === 'nv-has-children' || name === 'nv-open').forEach(name => node.classList.remove(name));
        node.removeAttribute('data-nv-ready');
        node.removeAttribute('data-nv-side');
        if (node.id.startsWith('NovaDropdown-')) node.removeAttribute('id');
        if ((node.getAttribute('aria-controls') || '').startsWith('NovaDropdown-')) {
          node.removeAttribute('aria-controls');
          node.removeAttribute('aria-expanded');
        }
      });
    });
  }
  function init() {
    roots.forEach((_, root) => {
      if (!root.isConnected) { close(root); roots.delete(root); }
    });
    document.querySelectorAll(rootSelector).forEach(connect);
    document.querySelectorAll('#header-outer[data-nova-dropdown]').forEach(wrapper => {
      preserveOverflowMenu(wrapper);
      if (wrappers.has(wrapper)) return;
      wrappers.add(wrapper);
      wrapper.addEventListener('click', handleClick, true);
      wrapper.addEventListener('keydown', handleKey, true);
      // Xclusive listens to touchstart as well as click. Prevent its desktop
      // toggle listener from firing twice; mobile events remain untouched.
      wrapper.addEventListener('touchstart', event => {
        touchUntil = Date.now() + 800;
        const root = event.target.closest('.nv-dropdown-item');
        if (active() && roots.has(root)) event.stopPropagation();
      }, { capture: true, passive: true });
      const observer = new MutationObserver(() => {
        if (!wrapper.isConnected) { observer.disconnect(); return; }
        preserveOverflowMenu(wrapper);
        wrapper.querySelectorAll(':is(#nav, #nav-bar) > ul[data-type] > .nv-dropdown-item').forEach(connect);
      });
      observer.observe(wrapper, { childList: true, subtree: true });
    });
  }
  function refresh() {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      roots.forEach((state, root) => {
        if (!active()) {
          // Never remove native .toggle/.mtoggle on a mobile resize: the
          // drawer may be open while the browser address bar changes height.
          clearTimeout(state.timer);
          root.classList.remove('nv-open', 'nv-long-panel');
          state.categories.forEach(category => {
            clearTimeout(category._nvCloseTimer);
            category.classList.remove('nv-open');
          });
          [root, ...state.categories].forEach(node => controls(node).forEach(control => {
            control.removeAttribute('aria-expanded');
          }));
        } else if (root.classList.contains('nv-open')) {
          fit(root);
          state.categories.filter(category => category.classList.contains('nv-open')).forEach(category => fitChild(root, category));
        }
      });
    });
  }
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'touch') touchUntil = Date.now() + 800;
    if (!active()) return;
    roots.forEach((_, root) => { if (!root.contains(event.target)) close(root); });
  }, { passive: true });
  document.addEventListener('focusin', event => {
    if (!active()) return;
    roots.forEach((_, root) => { if (!root.contains(event.target)) close(root); });
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => setTimeout(init, 0));
  window.addEventListener('nav', init);
  window.addEventListener('resize', refresh, { passive: true });
  window.addEventListener('scroll', refresh, { passive: true });
  desktop.addEventListener('change', refresh);
  window.NovaDropdownMenu = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
